/**
 * Multi-leg mileage itinerary (§A6g) — the saved journey behind a bid's
 * round-trip miles. The supervisor builds a per-day route (base → pickup
 * warehouse(s) → job site → debris disposal → base) in the calculator modal;
 * each leg's miles + route geometry come from Azure Maps (server-side). The
 * itinerary persists on the project (`bid_mileage_legs`, jsonb) so it's visible
 * and re-openable; the route GEOMETRY is NOT persisted (recomputed on open to
 * keep the blob small).
 */
import { z } from 'zod';
import { UuidSchema } from './common.js';
/** What a leg is for — drives its label/colour in the breakdown + on the map. */
export const LegCategorySchema = z.enum([
    'depart', // base → first stop of the day
    'pickup', // → a warehouse to load inventory
    'to_site', // → the job site
    'disposal', // → debris / disposal location
    'return', // → back to base
    'other', // user-added / re-categorized
]);
/** One endpoint of a leg — a place with coordinates. */
export const MileagePointSchema = z.object({
    label: z.string().max(200),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    /** Set when chosen from the locations registry (else a freeform address). */
    location_id: UuidSchema.nullable().optional(),
});
export const MileageLegSchema = z.object({
    /** 1-based day index (each day is its own base→…→base round trip). */
    day: z.number().int().min(1).max(60),
    category: LegCategorySchema,
    from: MileagePointSchema,
    to: MileagePointSchema,
    miles: z.number().nonnegative().max(100_000),
    /** true = straight-line haversine fallback (Azure unavailable), not a road route. */
    estimated: z.boolean().default(false),
});
export const MileageItinerarySchema = z.object({
    version: z.literal(1).default(1),
    legs: z.array(MileageLegSchema).max(200),
    total_miles: z.number().nonnegative().max(1_000_000),
    /** The vehicle's home base (start + end of each day). null until the
     *  supervisor picks it — no default. */
    base: MileagePointSchema.nullable(),
    include_disposal: z.boolean().default(false),
});
/** Round-trip miles for an itinerary — sum of leg miles, to a whole mile.
 *  Shared so the modal's displayed total and the server's stored
 *  bid_mileage_miles round identically. */
export function itineraryTotalMiles(legs) {
    return Math.round(legs.reduce((sum, l) => sum + (l.miles || 0), 0));
}
// ----------------------------------------------------------------------------
// POST /geo/route — compute per-leg driving miles + geometry via Azure Maps.
// One inner array per day (each day's ordered stops); each needs ≥2 stops.
// ----------------------------------------------------------------------------
export const RouteCoordSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
});
export const RouteRequestSchema = z.object({
    sequences: z.array(z.array(RouteCoordSchema).min(2).max(25)).min(1).max(31),
});
export const RouteLegResultSchema = z.object({
    miles: z.number().nonnegative(),
    /** Road-route geometry for drawing on the map (response-only, not persisted). */
    points: z.array(RouteCoordSchema),
    estimated: z.boolean(),
});
export const RouteResponseSchema = z.object({
    sequences: z.array(z.object({ legs: z.array(RouteLegResultSchema) })),
    total_miles: z.number().nonnegative(),
    estimated: z.boolean(),
});
//# sourceMappingURL=mileage.js.map